import { NgRedux } from '@angular-redux/store';
import { Injectable } from '@angular/core';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/shareReplay';
import 'rxjs/add/operator/take';
import { Observable } from 'rxjs/Observable';
import { IAppState } from '../../../app/store';
import { ReservationDao, ReservationRateRulesDao, ReservationV2Dao } from '../daos';
import { AssociationDao } from '../daos/association.dao';
import { IAssociationData, IAssociationProfile } from '../interfaces/HotelReservation/IAssociationData';
import { IHotelReservation } from '../interfaces/HotelReservation/IHotelReservation';
import { IHotelReservationV2 } from '../interfaces/HotelReservationV2/IHotelReservation-IV2';
import { ICommunicationPreferences } from '../interfaces/HotelReservationV2/ICommunicationPreferences';
import { InvalidReservationError } from '../model/errors/reservation-error.model';
import { ServiceError } from '../model/errors/service-error.model';
import { FeatureFlag } from '../model/featureflags/feature-flag.model';
import { FeatureName } from '../model/featureflags/feature-name.model';
import { ReservationChangeType } from '../model/reservation/change/reservation-change.model';
import { ReservationStatusType } from '../model/reservation/reservation-status.model';
import { Reservation } from '../model/reservation/reservation.model';
import { CommunicationPreferences } from '../model/communication/communication.preferences.model';
import { User } from '../model/user';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { BaseService } from './base.service';
import { ReservationModelBuilder } from './builders/reservation-model.builder';
import { ReservationRequestBuilder } from './builders/reservation-request.builder';
import { ReservationV2RequestBuilder } from './builders/reservation-v2-request.builder';
import { ReservationV2ModelBuilder } from './builders/reservationV2-model.builder';
import { CommunicationPreferencesRequestBuilder } from './builders/communication-preferences-request.builder';
import { LogService } from './logging.service';
import { AssociationErrorMapper } from './mapper/association-error.mapper';
import { ReservationErrorMapper } from './mapper/reservation-error.mapper';
import { ReservationV2ErrorMapper } from './mapper/reservation-v2-error.mapper';
import { ReservationServiceValidator } from './validators/reservation.validator';
import { ReservationV2ServiceValidator } from './validators/reservationV2.validator';

const RESERVATION_CACHE_DURATION = 10 * 60 * 1000; // 10 mins
const CLASS_NAME = 'ReservationService';

const SSO_COOKIE_NAME = 'X-IHG-SSO-TOKEN';

interface ByReservationId {
  confirmationNumber: string,
  lastName: string
}

@Injectable()
export class ReservationService extends BaseService<Reservation, ByReservationId> {
  private _reservationObservables: Map<string, Observable<Reservation>>;
  private _state: IAppState;
  private _featureFlags: Array<FeatureFlag>;

  constructor(
    private _reservationDao: ReservationDao,
    private _reservationV2Dao: ReservationV2Dao,
    private _reservationRateRulesDao: ReservationRateRulesDao,
    private _log: LogService,
    private _associationDao: AssociationDao,
    private _ngRedux: NgRedux<IAppState>,
    private _featureFlagsService: FeatureFlagsService
  ) {
    super(new ReservationErrorMapper(), RESERVATION_CACHE_DURATION);
    this._updateFromState();
    this._ngRedux.subscribe(() => {
      this._updateFromState();
    });
    this._reservationObservables = new Map<string, Observable<Reservation>>();
  }

  getReservation(confirmationNumber: string, lastName: string, useCache: boolean = false): Observable<Reservation> {
    return this.getByKey(<ByReservationId>{confirmationNumber: confirmationNumber, lastName: lastName}, useCache);
  }

  deleteReservation(reservationId: string, lastName: string, communicationPreferences: CommunicationPreferences): Observable<Reservation> {
    const featureToggle = this._featureFlagsService.checkFeatureFlag(FeatureName.RESERVATION_V2, '0.1.0', this._featureFlags);
    if (featureToggle) {
      const communicationPreferencesRequest: ICommunicationPreferences = new CommunicationPreferencesRequestBuilder()
      .withCommunicationPreferences(communicationPreferences)
      .build();
    return this._reservationV2Dao.patch(reservationId, lastName, communicationPreferencesRequest)
        .map((data) => {
          return new ReservationV2ModelBuilder().withReservationData(data).build();
        })
        .catch((error) => {
          const serviceError: ServiceError = new ReservationV2ErrorMapper().map(error);
          this._log.error(`Error when cancelling reservation ${reservationId}`, CLASS_NAME, serviceError);
          return Observable.throw(serviceError);
        });
    } else {
      return this._reservationDao.delete(reservationId, lastName);
    }
  }

  updateReservation(reservation: Reservation, reservationChanges: Map<ReservationChangeType, any>, availableReservation: Reservation, rateDetails: any): Observable<Reservation> {
    const featureToggle = this._featureFlagsService.checkFeatureFlag(FeatureName.RESERVATION_V2, '0.1.0', this._featureFlags);
    if (featureToggle) {
      const reservationV2ChangeRequest: IHotelReservationV2 = new ReservationV2RequestBuilder()
        .withReservation(reservation)
        .withChanges(reservationChanges)
        .withStatus(ReservationStatusType.COMMIT)
        .withIds(reservation)
        .withPayment(reservation, rateDetails)
        .withCommunicationPreferences(reservation)
        .build();
      return this._reservationV2Dao.put(reservation.confirmationNumber, reservationV2ChangeRequest)
        .map((data) => {
          return new ReservationV2ModelBuilder().withReservationData(data).build();
        })
        .catch((error) => {
          const serviceError: ServiceError = new ReservationV2ErrorMapper().map(error);
          this._log.error(`Error when updating reservation ${reservation.confirmationNumber}`, CLASS_NAME, serviceError);
          return Observable.throw(serviceError);
        });
    } else {
      const reservationChangeRequest: IHotelReservation = new ReservationRequestBuilder()
        .withReservation(reservation)
        .withReservationRateChanges(availableReservation.rate, availableReservation.hotelMnemonic)
        .withChanges(reservationChanges)
        .withStatus(ReservationStatusType.COMMIT)
        .withCommunicationPreferences(reservation.communicationPreferences)
        .withSpecialNightRate(reservation.specialNightRate)
        .build();
      return this._reservationDao.put(reservationChangeRequest)
        .map((data) => {
          return new ReservationModelBuilder().withReservationData(data).build();
        })
        .catch((error) => {
          const serviceError: ServiceError = this.mapError(error);
          this._log.error(`Error when updating reservation ${reservation.confirmationNumber}`, CLASS_NAME, serviceError);
          return Observable.throw(serviceError);
        });
    }
  }

  associate(reservation: Reservation, user: User, overrideGuest: boolean): Observable<Reservation> {
    const associateData: IAssociationData = {
      'primaryLastName': reservation.guest.lastName,
      'overrideGuest': overrideGuest,
      'userProfiles': [
        <IAssociationProfile>{
          id: 1,
          userFirstName: user.firstName,
          userLastName: user.lastName,
          loyaltyId: user.rewardsClubNumber,
        }
      ]
    };
    const reservationId = reservation.confirmationNumber;
    return this._associationDao.put(associateData, reservationId)
      .map((data) => {
        return new ReservationModelBuilder().withReservationData(data).build();
      })
      .catch((error) => {
        const serviceError: ServiceError = new AssociationErrorMapper().map(error);
        this._log.error(`Error when associate reservation ${reservation.confirmationNumber}`, CLASS_NAME, serviceError);
        return Observable.throw(serviceError);
      });
  }

  private _updateFromState() {
    this._state = this._ngRedux.getState();
    this._featureFlags = this._state.featureFlags.flags;
  }

  protected generateKey(reservationId: ByReservationId): string {
    return reservationId.confirmationNumber + reservationId.lastName;
  }

  protected getFreshObservable(reservationId: ByReservationId): Observable<Reservation> {
    const featureToggle = this._featureFlagsService.checkFeatureFlag(FeatureName.RESERVATION_V2, '0.1.0', this._featureFlags);
    if (featureToggle) {
      return this._reservationV2Dao.get(reservationId.confirmationNumber, reservationId.lastName)
        .map((data) => {
          if (!ReservationV2ServiceValidator.validateReservationData(data)) {
            this._log.error('Not enough reservation data to retrieve the reservation information');
            throw new InvalidReservationError('Not enough reservation data to retrieve the reservation information');
          }
          return new ReservationV2ModelBuilder().withReservationData(data).build();
        })
        .catch((error) => {
          const serviceError: ServiceError = new ReservationV2ErrorMapper().map(error);
          this._log.error(`Error when building reservationV2 ${reservationId.confirmationNumber} - ${reservationId.lastName}`, CLASS_NAME, serviceError);
          return Observable.throw(serviceError);
        });
    } else {

      return this._reservationDao.get(reservationId.confirmationNumber, reservationId.lastName)
        .mergeMap((reservationData: any) => {
          if (!ReservationServiceValidator.validateReservationData(reservationData)) {
            this._log.error('Not enough reservation data to retrieve the reservation information');
            throw new InvalidReservationError('Not enough reservation data to retrieve the reservation information');
          }
          const hotelMnemonic = reservationData.stay.hotelMnemonic;
          return this._reservationRateRulesDao.get(reservationId.confirmationNumber, reservationId.lastName, hotelMnemonic)
            .map((reservationRulesData: any) => {
              return new ReservationModelBuilder().withReservationData(reservationData).withRateRulesData(reservationRulesData).build();
            })
            .catch((error) => {
              const serviceError: ServiceError = this.mapError(error);
              this._log.error(`Error when building reservation ${reservationId.confirmationNumber} - ${reservationId.lastName}`, CLASS_NAME, serviceError);
              return Observable.throw(serviceError);
            });
        })
        .catch((error) => {
          const serviceError: ServiceError = this.mapError(error);
          this._log.error(`Error when getting reservation ${reservationId.confirmationNumber} - ${reservationId.lastName}`, CLASS_NAME, serviceError);
          return Observable.throw(serviceError);
        });

    }

  }
}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/api/services/reservation.service.ts